<template>
    <div class="select-container" :class="{ 'is-disabled': disabled }">
        <label v-if="label" :for="id" class="select-label">
            {{ label }}
            <span v-if="required">*</span>
        </label>
        <div
            class="select-wrapper"
            :class="[{ 'select-open': isOpen, 'select-error': error }, variantClass]"
            @click="toggleDropdown"
            @keydown.enter.prevent="toggleDropdown"
            @keydown.space.prevent="toggleDropdown"
            @keydown.up.prevent="highlightPrevOption"
            @keydown.down.prevent="highlightNextOption"
            @keydown.esc="closeDropdown"
            @keydown.tab="closeDropdown"
            tabindex="0"
            :aria-expanded="isOpen"
            :aria-labelledby="labelId"
            :aria-describedby="helpTextId"
            role="combobox"
            ref="selectElement">
            <span
                class="select-value"
                :id="labelId"
                :class="{ 'text-green-600': !modelValue && variant === 'checkout' }">
                {{ selectedLabel || placeholder }}
            </span>
            <span class="select-arrow" aria-hidden="true">
                <svg
                    v-if="variant !== 'checkout' && variant !== 'rounded'"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round">
                    <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
            </span>
            <div v-show="isOpen" class="select-dropdown" role="listbox" :id="dropdownId">
                <div
                    v-for="(option, index) in options"
                    :key="option.value"
                    class="select-option"
                    :class="{
                        selected: modelValue === option.value,
                        highlighted: highlightedIndex === index,
                    }"
                    @click.stop="selectOption(option.value)"
                    @mouseenter="highlightedIndex = index"
                    role="option"
                    :aria-selected="modelValue === option.value"
                    :id="`${id}-option-${index}`"
                    tabindex="-1">
                    {{ option.label }}
                </div>
            </div>
        </div>
        <span class="text-xs font-normal text-green-900" v-if="!required && variant === 'checkout'">{{
            $snippets?.['headless.input.optional'] || 'Optional'
        }}</span>
        <p v-if="helpText" class="select-help-text" :id="helpTextId">{{ helpText }}</p>
        <p v-if="error" class="select-error-text" :id="`${id}-error`" aria-live="polite">{{ error }}</p>
    </div>
</template>

<script setup lang="ts">
    interface SelectOption {
        value: string | number;
        label: string;
    }

    const props = defineProps({
        modelValue: {
            type: [String, Number],
            default: '',
        },
        options: {
            type: Array as () => SelectOption[],
            default: () => [],
            required: true,
        },
        placeholder: {
            type: String,
            default: 'Select an option',
        },
        label: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: '',
        },
        helpText: {
            type: String,
            default: '',
        },
        id: {
            type: String,
            default: () => `select-${useUniqueId()}`,
        },
        required: {
            type: Boolean,
            default: false,
        },
        variant: {
            type: String,
            default: 'default', // 'default', 'checkout', or 'rounded'
            validator: (value: string) => ['default', 'checkout', 'rounded'].includes(value),
        },
    });

    const emit = defineEmits(['update:modelValue', 'change']);

    const isOpen = ref(false);
    const highlightedIndex = ref(-1);
    const selectElement = ref<HTMLElement | null>(null);

    const labelId = computed(() => `${props.id}-label`);
    const dropdownId = computed(() => `${props.id}-dropdown`);
    const helpTextId = computed(() => (props.helpText ? `${props.id}-helptext` : undefined));

    const selectedOption = computed(() => {
        return props.options.find(option => option.value === props.modelValue);
    });

    const selectedLabel = computed(() => {
        return selectedOption.value?.label || '';
    });

    const variantClass = computed(() => {
        switch (props.variant) {
            case 'checkout':
                return 'checkout-style border-none';
            case 'rounded':
                return 'rounded-style';
            default:
                return 'default-style';
        }
    });

    function toggleDropdown() {
        if (props.disabled) return;
        isOpen.value = !isOpen.value;

        if (isOpen.value) {
            highlightSelectedOption();
            document.addEventListener('click', handleOutsideClick);
        } else {
            document.removeEventListener('click', handleOutsideClick);
        }
    }

    function closeDropdown() {
        isOpen.value = false;
        document.removeEventListener('click', handleOutsideClick);
    }

    function handleOutsideClick(e: MouseEvent) {
        if (selectElement.value && !selectElement.value.contains(e.target as Node)) {
            closeDropdown();
        }
    }

    function selectOption(value: string | number) {
        if (value !== props.modelValue) {
            emit('update:modelValue', value);
            emit('change', value);
        }
        closeDropdown();
    }

    function highlightSelectedOption() {
        const selectedIndex = props.options.findIndex(option => option.value === props.modelValue);
        highlightedIndex.value = selectedIndex >= 0 ? selectedIndex : 0;
        nextTick(() => {
            scrollOptionIntoView();
        });
    }

    function highlightNextOption() {
        if (!isOpen.value) {
            toggleDropdown();
            return;
        }

        if (highlightedIndex.value < props.options.length - 1) {
            highlightedIndex.value++;
            scrollOptionIntoView();
        }
    }

    function highlightPrevOption() {
        if (!isOpen.value) {
            toggleDropdown();
            return;
        }

        if (highlightedIndex.value > 0) {
            highlightedIndex.value--;
            scrollOptionIntoView();
        }
    }

    function scrollOptionIntoView() {
        nextTick(() => {
            const dropdown = document.getElementById(dropdownId.value);
            const highlighted = document.getElementById(`${props.id}-option-${highlightedIndex.value}`);

            if (dropdown && highlighted) {
                const dropdownRect = dropdown.getBoundingClientRect();
                const highlightedRect = highlighted.getBoundingClientRect();

                if (highlightedRect.bottom > dropdownRect.bottom) {
                    dropdown.scrollTop += highlightedRect.bottom - dropdownRect.bottom;
                } else if (highlightedRect.top < dropdownRect.top) {
                    dropdown.scrollTop -= dropdownRect.top - highlightedRect.top;
                }
            }
        });
    }

    watch(highlightedIndex, newIndex => {
        if (isOpen.value && newIndex >= 0) {
            // Allow selecting with enter key when an option is highlighted
            const handleKeydown = (e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    selectOption(props.options[newIndex].value);
                }
            };

            document.addEventListener('keydown', handleKeydown);
            return () => document.removeEventListener('keydown', handleKeydown);
        }
    });

    onMounted(() => {
        // Add an event listener to close the dropdown when the window is resized
        window.addEventListener('resize', closeDropdown);
    });

    onBeforeUnmount(() => {
        document.removeEventListener('click', handleOutsideClick);
        window.removeEventListener('resize', closeDropdown);
    });
</script>

<style scoped>
    @reference "tailwindcss";

    .select-wrapper.default-style {
        @apply relative flex min-h-[42px] w-full cursor-pointer items-center rounded-sm border border-solid border-green-600 bg-white px-3 py-2 shadow-sm transition-colors;
    }

    .select-wrapper.default-style.select-open {
        @apply border-green-800;
    }

    .select-wrapper.default-style:focus,
    .select-wrapper.default-style:focus-within {
        @apply ring-2 ring-green-600/35 ring-offset-1 ring-offset-white outline-none;
    }

    .select-wrapper.rounded-style {
        @apply relative cursor-pointer appearance-none rounded-full border-2 border-solid border-green-600 bg-[url('/icons/chevron-down.svg')] bg-[center_right_12px] bg-no-repeat px-3 py-2 pr-8;
    }

    .select-wrapper.rounded-style .select-value {
        @apply text-sm;
    }

    .select-wrapper.rounded-style .select-arrow {
        @apply hidden;
    }

    .select-wrapper.rounded-style .select-dropdown {
        @apply absolute top-full right-0 left-0 z-50 mt-1 max-h-60 overflow-auto rounded-2xl border-2 border-green-600 bg-white py-2 shadow-md;
    }

    .select-wrapper.rounded-style .select-option {
        @apply cursor-pointer px-4 py-2 text-sm transition-colors;
    }

    .select-wrapper.rounded-style .select-option:hover {
        @apply bg-gray-100;
    }

    .select-wrapper.rounded-style .select-option.selected {
        @apply font-medium;
    }

    .select-wrapper.rounded-style .select-option.highlighted {
        @apply bg-gray-100;
    }

    /* Checkout style (matching form inputs) */
    .select-wrapper.checkout-style {
        @apply relative flex h-[36px] w-full cursor-pointer items-center rounded-sm border border-solid border-black bg-white px-3 py-2 transition-all;
        @apply appearance-none bg-[url('/assets/icons/chevron-down-solid.svg')] bg-[center_right_19px] bg-no-repeat;
        background-size: 10px;

        &:hover {
            @apply border-black;
        }

        &:focus,
        &:focus-within {
            @apply border-black ring-0 outline-none;
        }

        &.select-open {
            @apply border-black bg-[url('/assets/icons/chevron-up-solid.svg')];
        }

        &.select-error {
            @apply border-red-600 bg-red-100 text-red-700;
        }

        &.disabled {
            @apply border-green-400 bg-green-300 text-green-400;
        }
    }

    .select-value {
        @apply flex-1 overflow-hidden text-sm font-medium text-ellipsis whitespace-nowrap text-black;
    }

    .select-arrow {
        @apply ml-2 flex items-center text-gray-500 transition-transform duration-200;

        .select-open & {
            @apply rotate-180 transform;
        }

        /* Arrow is handled by background image in checkout style */
        .checkout-style & {
            @apply hidden;
        }
    }

    .select-dropdown {
        @apply absolute left-0 z-50 w-full overflow-y-auto border;
        top: calc(100% + 0.25rem);
        max-height: 16rem;
        @apply border-green-300 bg-white shadow-md;
    }

    .select-option {
        @apply cursor-pointer px-3.5 py-2.5 text-sm transition-colors duration-150;

        &:hover,
        &.highlighted {
            @apply bg-green-400;
        }

        &.selected {
            @apply bg-green-600 font-medium text-white;
        }
    }

    .select-help-text {
        @apply mt-1.5 text-xs text-gray-500;
    }

    .select-error-text {
        @apply mt-1.5 text-xs text-red-600;
    }

    .is-disabled {
        .select-wrapper {
            @apply cursor-not-allowed;
        }

        .default-style {
            @apply border-gray-200 bg-gray-100 text-gray-400 shadow-none;

            &:hover {
                @apply border-gray-200 shadow-none;
            }
        }
    }

    @media (max-width: 768px) {
        .select-dropdown {
            max-height: 12rem;
        }

        .select-option {
            @apply py-3; /* Larger touch target for mobile */
        }
    }
</style>
